/**
 * Fez Template Compiler
 *
 * Compiles Fez templates to render functions.
 * Supports legacy {{ }} syntax via auto-conversion.
 *
 * Syntax:
 *   {expression}        - Output escaped value
 *   {@html expr}        - Output raw HTML
 *   {@json expr}        - Output formatted JSON
 *   {#if cond}...{/if}  - Conditional
 *   {#each arr as item} - Loop
 *   {#for item in arr}  - Loop (alt syntax)
 */

import createTemplateCompiler from './template-compiler.js';

// Template cache
const cache = new Map();

// =============================================================================
// MAIN EXPORT
// =============================================================================

/**
 * Create a template render function
 *
 * @param {string} text - Template HTML
 * @param {Object} opts - { name: componentName }
 * @returns {Function} Render function (ctx) => html
 */
export default function createTemplate(text, opts = {}) {
  // Cache key includes opts: the same template text compiled for a different
  // component (name) or strict mode yields a different render function.
  const cacheKey = `${opts.name || ''}\u0000${opts.strict ? 1 : 0}\u0000${text}`;
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const normalized = normalizeTemplateText(text, opts);
  const fn = createTemplateCompiler(normalized, opts);
  cache.set(cacheKey, fn);
  return fn;
}

/**
 * Clear template cache (for testing)
 */
export function clearTemplateCache() {
  cache.clear();
}

/**
 * Normalize template text before cache lookup and compilation.
 */
function normalizeTemplateText(text, opts = {}) {
  if (hasLegacySyntax(text)) {
    return convertLegacySyntax(text, opts.name);
  }
  return text;
}

// =============================================================================
// LEGACY SYNTAX SUPPORT
// =============================================================================

/**
 * Check if text uses old {{ }} syntax
 */
function hasLegacySyntax(text) {
  return text.includes('{{') && text.includes('}}');
}
/**
 * Convert {{ }} syntax to { } syntax
 *
 * Mappings:
 *   {{ expr }}      -> {expr}
 *   {{if cond}}     -> {#if cond}
 *   {{for x in y}}  -> {#for x in y}
 *   {{each y as x}} -> {#each y as x}
 *   {{raw x}}       -> {@html x}
 *   {{json x}}      -> {@json x}
 *   {{block x}}     -> {@block x}
 */
function convertLegacySyntax(text, componentName) {
  // Blocks
  text = text.replace(/\{\{block\s+(\w+)\s*\}\}/g, '{@block $1}');
  text = text.replace(/\{\{\/block\}\}/g, '{/block}');
  text = text.replace(/\{\{block:([\w\-]+)\s*\}\}/g, '{@block:$1}');

  // Conditionals
  text = text.replace(/\{\{#?if\s+(.*?)\}\}/g, '{#if $1}');
  text = text.replace(/\{\{\/if\}\}/g, '{/if}');
  text = text.replace(/\{\{#?unless\s+(.*?)\}\}/g, '{#unless $1}');
  text = text.replace(/\{\{\/unless\}\}/g, '{/unless}');
  text = text.replace(/\{\{:?else\s+if\s+(.*?)\}\}/g, '{:else if $1}');
  text = text.replace(/\{\{:?elsif\s+(.*?)\}\}/g, '{:else if $1}');
  text = text.replace(/\{\{:?elseif\s+(.*?)\}\}/g, '{:else if $1}');
  text = text.replace(/\{\{:?else\}\}/g, '{:else}');

  // Loops
  text = text.replace(/\{\{#?for\s+(.*?)\}\}/g, '{#for $1}');
  text = text.replace(/\{\{\/for\}\}/g, '{/for}');
  text = text.replace(/\{\{#?each\s+(.*?)\}\}/g, '{#each $1}');
  text = text.replace(/\{\{\/each\}\}/g, '{/each}');

  // Special directives
  text = text.replace(/\{\{#?(?:raw|html)\s+(.*?)\}\}/g, '{@html $1}');
  text = text.replace(/\{\{json\s+(.*?)\}\}/g, '{@json $1}');

  // Expressions
  text = text.replace(/\{\{\s*(.*?)\s*\}\}/g, '{$1}');

  // Log warning
  if (componentName) {
    console.warn(`Fez component "${componentName}" uses old {{ ... }} notation, converting.`);
  }

  return text;
}
